import { type Action as Permission } from '@canvas/model/permissions';

type Subscriber = VoidFunction;

type PermissionsManagerOpts = {
  /**
   * Unique identifier for the document the {@link claims} are associated with.
   */
  id: string;
  /**
   * Permissions claims yielded from an upstream system (e.g. Confluence
   * Monolith). Represent what a user has been granted to do.,
   */
  claims: Set<Permission>;
};

export class PermissionsManager {
  /**
   * Current document ID for which a set of claims have been granted.
   */
  #id: string | undefined;

  /**
   * Currently granted claims from an upstream system.
   */
  #claims: Set<Permission> = new Set();
  /**
   * Subscribers to notify when there are any changes in claims granted to a user.
   */
  #subscribers: Subscriber[] = [];

  public constructor(opts?: PermissionsManagerOpts) {
    if (!opts) {
      return;
    }

    this.#id = opts.id;
    this.#claims = opts.claims;
  }

  get id() {
    return this.#id;
  }

  /**
   * @deprecated - only used for testing purposes. For all updates, use {@link update}
   */
  set id(id: string | undefined) {
    if (process.env.NODE_ENV !== 'test') {
      throw Error('cannot set id programatically outside of testing environments.');
    }

    this.#id = id;
  }

  get claims() {
    return this.#claims;
  }

  /**
   * Updates the current claims a user has been granted on a specific document.
   * @param id
   * @param permissions
   */
  update(id: string | undefined, permissions: Set<Permission>) {
    this.#id = id;
    this.#claims.clear();

    for (const permission of permissions) {
      this.#claims.add(permission);
    }

    this.notifySubscribers();
  }

  /**
   * Subscribe to changes in permissions granted to a user.
   * @param subscriber
   * @returns
   */
  subscribe(subscriber: Subscriber) {
    this.#subscribers.push(subscriber);

    return () => this.unsubscribe(subscriber);
  }

  reset() {
    this.#claims.clear();
    this.notifySubscribers();
  }

  destroy() {
    this.#claims.clear();
    this.#subscribers.length = 0;
  }

  private unsubscribe(subscriber: Subscriber) {
    const idx = this.#subscribers.findIndex((s) => s === subscriber);
    if (idx === -1) {
      return;
    }

    this.#subscribers.splice(idx, 1);
  }

  private notifySubscribers() {
    this.#subscribers.forEach((subscriber) => subscriber());
  }
}
